import React, { createContext, useContext, useEffect, useState } from 'react';
import { useCheckSessionQuery } from '../store';
import { Spin } from 'antd';
import { IAuthenticatedUser } from '../pages/Requisitions/constants/requisition.interface';
import {
  AuthenticationMethod,
  IActiveSession,
} from '../store/rtkQuery/auth';
import { setChatbotToken, removeChatbotToken, loadChatbotScript, unloadChatbotScript } from '../utils';
import { saveAssetToken } from '../services/knowledgeHub';
interface AuthContextProps {
  isAuthenticated: boolean | null;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  user: IAuthenticatedUser | null;
  setUser: React.Dispatch<React.SetStateAction<IAuthenticatedUser | null>>;
  authenticationMethod: AuthenticationMethod | null;
  setAuthenticationMethod: React.Dispatch<
    React.SetStateAction<AuthenticationMethod | null>
  >;
  isLoading: boolean;
  /** Set when login fails due to device limit — holds the active sessions list */
  sessionLimitData: {
    activeSessions: IActiveSession[];
    maxSessions: number;
    email: string;
    password?: string; // encrypted; omitted for OTP flow
  } | null;
  setSessionLimitData: React.Dispatch<
    React.SetStateAction<AuthContextProps['sessionLimitData']>
  >;
  clearSessionLimitData: () => void;
}

const SESSION_LIMIT_STORAGE_KEY = 'session_limit_data';

/** Persist session limit data to sessionStorage (survives navigation, cleared on tab close) */
function persistSessionLimitData(data: AuthContextProps['sessionLimitData']) {
  if (data) {
    sessionStorage.setItem(SESSION_LIMIT_STORAGE_KEY, JSON.stringify(data));
  } else {
    sessionStorage.removeItem(SESSION_LIMIT_STORAGE_KEY);
  }
}

/** Read session limit data from sessionStorage on initial load */
function readPersistedSessionLimitData(): AuthContextProps['sessionLimitData'] {
  try {
    const raw = sessionStorage.getItem(SESSION_LIMIT_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    sessionStorage.removeItem(SESSION_LIMIT_STORAGE_KEY);
  }
  return null;
}

const AuthContext = createContext<AuthContextProps | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('User not found. Please reload the page.');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<IAuthenticatedUser>(null);
  const [authenticationMethod, setAuthenticationMethod] =
    useState<AuthenticationMethod | null>(null);
  const [sessionLimitData, _setSessionLimitData] = useState<
    AuthContextProps['sessionLimitData']
  >(readPersistedSessionLimitData);

  /** Wrap setter so every update is also mirrored to sessionStorage */
  const setSessionLimitData: React.Dispatch<
    React.SetStateAction<AuthContextProps['sessionLimitData']>
  > = (value) => {
    _setSessionLimitData((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      persistSessionLimitData(next);
      return next;
    });
  };

  const checkSessionResponse = useCheckSessionQuery();

  useEffect(() => {
    if (
      checkSessionResponse.isSuccess &&
      !checkSessionResponse.isFetching &&
      checkSessionResponse.data?.data?.isAuthenticated
    ) {
      // User has a valid session — clear any stale device-limit state
      setSessionLimitData(null);
      setIsAuthenticated(true);
      setUser(checkSessionResponse.data?.data?.user);
      setAuthenticationMethod(
        checkSessionResponse.data?.data?.authenticationMethod ?? null,
      );
      setChatbotToken(checkSessionResponse.data?.data?.chatbotToken);
      saveAssetToken(checkSessionResponse.data?.data?.assetToken);
      loadChatbotScript();
    }

    if (
      checkSessionResponse.isSuccess &&
      !checkSessionResponse.isFetching &&
      checkSessionResponse.data?.data?.isAuthenticated === false
    ) {
      setIsAuthenticated(false);
      setUser(null);
      removeChatbotToken();
      localStorage.removeItem('assetToken');
      unloadChatbotScript();
    }

    if (checkSessionResponse.isError && !checkSessionResponse.isFetching) {
      setIsAuthenticated(false);
      setUser(null);
      setAuthenticationMethod(null);
      removeChatbotToken();
      localStorage.removeItem('assetToken');
      unloadChatbotScript();
    }
  }, [checkSessionResponse]);

  const clearSessionLimitData = () => setSessionLimitData(null);

  let content = children;
  if (checkSessionResponse.isFetching || isAuthenticated === null) {
    content = (
      <>
        <Spin
          tip={
            <div className="text-white/70 text-sm font-normal mt-2">
              Please wait..
            </div>
          }
          size="large"
          fullscreen
        />
      </>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        user,
        setUser,
        authenticationMethod,
        setAuthenticationMethod,
        isLoading: checkSessionResponse.isFetching,
        sessionLimitData,
        setSessionLimitData,
        clearSessionLimitData,
      }}
    >
      {content}
    </AuthContext.Provider>
  );
};
